import React from 'react'
import styled from '@emotion/styled'
import PropTypes from 'prop-types'
import fallbackImg from 'assets/images/ImageFallbackNR.jpeg'
import { addBlurToImage } from 'utils/helpers'

const VenueImageBlurContainer = styled('div')`
  ${({ pictureUrl }) =>
    `background: url("${pictureUrl}") center / cover no-repeat, url("${fallbackImg}") center / cover no-repeat;`}

  overflow: hidden;
  position: relative;
  height: 100%;
  width: 100%;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    opacity: 0;
    animation: pulse 1.5s infinite;
    background-color: white;
    filter: blur(20px);
  }

  @keyframes pulse {
    0% {
      opacity: 0;
    }
    50% {
      opacity: 0.2;
    }
    100% {
      opacity: 0;
    }
  }

  &.loaded::before {
    animation: none;
    content: none;
  }

  & img {
    opacity: 0;
    position: absolute;
    inset: 0;
    height: 100%;
    width: 100%;
    object-fit: cover;
    transition: opacity 250ms ease-in-out;
  }

  &.loaded img {
    opacity: 1;
  }
`

export const FallbackBlurImage = ({ pictureUrl, ...passingProps }) => {
  const [loaded, setIsLoaded] = React.useState(false)

  return (
    <VenueImageBlurContainer
      pictureUrl={addBlurToImage(pictureUrl)}
      className={loaded ? 'loaded' : ''}
      {...passingProps}
    >
      <img
        src={pictureUrl}
        alt="venue"
        loading="lazy"
        onLoad={() => setIsLoaded(true)}
      />
    </VenueImageBlurContainer>
  )
}

FallbackBlurImage.propTypes = {
  pictureUrl: PropTypes.string.isRequired,
}

const MAX_RETRIES = 2
const RETRY_DELAY_MS = 1500

const RETRYING = 'retrying'
const LOADED = 'loaded'
const FAILED = 'failed'

const FallbackImgWrapper = styled('div')`
  position: relative;
  overflow: hidden;
  height: 100%;
  width: 100%;

  &.${RETRYING} {
    background: #e8ebf0;
  }

  &.${RETRYING}::before {
    content: '';
    position: absolute;
    inset: 0;
    animation: fallback-pulse 1.5s ease-in-out infinite;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent);
  }

  @keyframes fallback-pulse {
    0% { transform: translateX(-100%); }
    100% { transform: translateX(100%); }
  }

  & > img {
    height: 100%;
    width: 100%;
    object-fit: cover;
  }

  &.${RETRYING} > img {
    visibility: hidden;
  }
`

export const FallbackImg = ({ alt = 'fallback image', src, className, style, ...props }) => {
  const retriesRef = React.useRef(0)
  const originalSrcRef = React.useRef(src)
  const [status, setStatus] = React.useState(null)

  React.useEffect(() => {
    originalSrcRef.current = src
    retriesRef.current = 0
    setStatus(null)
  }, [src])

  const handleError = React.useCallback((e) => {
    const imgEl = e.target
    if (retriesRef.current < MAX_RETRIES) {
      retriesRef.current += 1
      setStatus(RETRYING)
      const retrySrc = originalSrcRef.current
      setTimeout(() => {
        imgEl.src = `${retrySrc}${retrySrc.includes('?') ? '&' : '?'}r=${retriesRef.current}`
      }, RETRY_DELAY_MS)
    } else {
      imgEl.onerror = null
      imgEl.src = fallbackImg
      setStatus(FAILED)
    }
  }, [])

  const handleLoad = React.useCallback(() => {
    setStatus(LOADED)
  }, [])

  return (
    <FallbackImgWrapper className={`${status || ''} ${className || ''}`} style={style}>
      <img {...props} src={src} alt={alt} onError={handleError} onLoad={handleLoad} />
    </FallbackImgWrapper>
  )
}

FallbackImg.propTypes = {
  alt: PropTypes.string,
  src: PropTypes.string,
  className: PropTypes.string,
  style: PropTypes.object,
}
